import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { messages, type Locale } from "../i18n";
import { request } from "../lib/api";

export interface SessionUser { id: string; name: string; email: string; emailVerified: boolean; systemRole: string }
export interface Workspace { id: string; name: string; slug: string; status: string; plan_id: string; role: string }

interface AppContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: typeof messages.th | typeof messages.en;
  theme: "light" | "dark";
  toggleTheme: () => void;
  user: SessionUser | null;
  workspaces: Workspace[];
  workspace: Workspace | null;
  setWorkspace: (workspace: Workspace) => void;
  loading: boolean;
  refreshSession: () => Promise<void>;
  signOut: () => Promise<void>;
  api: <T>(path: string, init?: RequestInit) => Promise<T>;
}

const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => localStorage.getItem("paymeqr-locale") === "en" ? "en" : "th");
  const [theme, setTheme] = useState<"light" | "dark">(() => localStorage.getItem("paymeqr-theme") === "light" ? "light" : "dark");
  const [user, setUser] = useState<SessionUser | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [workspace, setWorkspaceState] = useState<Workspace | null>(null);
  const [loading, setLoading] = useState(true);

  const setLocale = useCallback((next: Locale) => { setLocaleState(next); localStorage.setItem("paymeqr-locale", next); document.documentElement.lang = next; }, []);
  const toggleTheme = useCallback(() => setTheme((current) => { const next = current === "dark" ? "light" : "dark"; localStorage.setItem("paymeqr-theme", next); return next; }), []);
  useEffect(() => { document.documentElement.dataset.theme = theme; document.documentElement.lang = locale; }, [theme, locale]);

  const refreshSession = useCallback(async () => {
    setLoading(true);
    try {
      const session = await request<{ user: SessionUser | null }>("/api/session");
      setUser(session.user);
      if (session.user === null) { setWorkspaces([]); setWorkspaceState(null); return; }
      const workspaceResult = await request<{ data: Workspace[] }>("/api/workspaces");
      setWorkspaces(workspaceResult.data);
      const saved = localStorage.getItem("paymeqr-workspace");
      const selected = workspaceResult.data.find((item) => item.id === saved) ?? workspaceResult.data[0] ?? null;
      setWorkspaceState(selected);
      if (selected === null) localStorage.removeItem("paymeqr-workspace");
      else localStorage.setItem("paymeqr-workspace", selected.id);
    } catch { setUser(null); setWorkspaces([]); setWorkspaceState(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { void refreshSession(); }, [refreshSession]);
  const setWorkspace = useCallback((next: Workspace) => { setWorkspaceState(next); localStorage.setItem("paymeqr-workspace", next.id); }, []);
  const signOut = useCallback(async () => { await request("/api/auth/sign-out", { method: "POST", body: "{}" }); setUser(null); setWorkspaces([]); setWorkspaceState(null); localStorage.removeItem("paymeqr-workspace"); }, []);
  const api = useCallback(<T,>(path: string, init?: RequestInit) => request<T>(path, init, workspace?.id), [workspace?.id]);

  const value = useMemo<AppContextValue>(() => ({ locale, setLocale, t: messages[locale], theme, toggleTheme, user, workspaces, workspace, setWorkspace, loading, refreshSession, signOut, api }), [locale, setLocale, theme, toggleTheme, user, workspaces, workspace, setWorkspace, loading, refreshSession, signOut, api]);
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp(): AppContextValue {
  const context = useContext(AppContext);
  if (context === null) throw new Error("useApp must be used inside AppProvider");
  return context;
}
